import { Injectable } from '@angular/core';
import { Alert, AlertType } from '@models/app/alert/Alert';
import { Subject, BehaviorSubject } from 'rxjs';
import { LocalizeService } from '@services/helpers/localize/localize.service';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  private defaultId = 'default-alert';

  // Subjects for alert events and removals
  private subject = new Subject<Alert>();
  private removeAlertSubject = new Subject<{ id: string }>();

  // Internal alert list maintained via a BehaviorSubject
  private _alerts: Alert[] = [];
  private _alertsSubject = new BehaviorSubject<Alert[]>(this._alerts);
  public alerts$ = this._alertsSubject.asObservable();

  constructor(private localize: LocalizeService) {}

  success(title: string, options?: any): void {
    this.alert(new Alert({ ...options, type: AlertType.Success, title }));
  }

  error(title: string, options?: any): void {
    this.alert(new Alert({ ...options, type: AlertType.Error, title }));
  }

  errorGeneric(): void {
    this.alert(
      new Alert({
        title: this.localize.l('ErrorProcessingRequest', 'Error processing request'),
        type: AlertType.Error,
      })
    );
  }

  danger(title: string, options?: any): void {
    this.error(title, options);
  }

  info(title: string, options?: any): void {
    this.alert(new Alert({ ...options, type: AlertType.Info, title }));
  }

  warn(title: string, options?: any): void {
    this.alert(new Alert({ ...options, type: AlertType.Warning, title }));
  }

  // Main alert method with auto-close functionality
  alert(alert: Alert): void {
    alert.id = alert.id || this.defaultId;
    this.subject.next(alert);
    this._alerts.push(alert);
    this._alertsSubject.next([...this._alerts]);

    // If autoClose is true, schedule removal after a delay (default 5000 ms)
    if (alert.autoClose) {
      const delay = alert.autoClose || 5000;
      setTimeout(() => {
        this.removeAlert(alert);
      }, delay);
    }
  }

  clear(): void {
    this._alertsSubject.next([])
  }

  removeAlert(alert: Alert): void {
    this._alerts = this._alerts.filter((a) => a.id !== alert.id);
    this._alertsSubject.next([...this._alerts]);
    this.removeAlertSubject.next({ id: alert.id });
  }
}
